/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useState } from 'react'
import { Alert, Button, Input } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { weightSemiBold } from 'src/components/css'
import { guardMessage } from '../utils/balanceError'
import { balanceLabel, balanceText } from '../utils/balanceI18n'

const NOTE_MAX_LENGTH = 500

type SlotNoteEditorProps = {
  /** The stored note for the occupant this editor is bound to. */
  note: string | null
  /**
   * The occupant captured when this editor MOUNTED. Sent verbatim with the save
   * and never re-derived — see the remount contract in the component docblock.
   */
  playerRegistrationId: string
  onSave: (
    note: string | null,
    expectedPlayerRegistrationId: string
  ) => Promise<unknown>
  saving?: boolean
}

const labelStyle = css`
  ${weightSemiBold};
  font-size: 11px;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: ${colors.NEUTRAL_70};
`

const hintStyle = css`
  font-size: 12px;
  color: ${colors.NEUTRAL_70};
`

/**
 * SN-4709 A2 — the rep's free-form note about the player in this slot ("he told
 * me he was going to pay on Friday"). Internal: players never see it.
 *
 * EXPLICIT SAVE, deliberately — no debounced autosave. This sits inside a money
 * record beside void and verify actions; a keystroke that silently issues a PUT
 * against a balance sheet is the wrong default there, and an explicit button is
 * also what makes "saved / not saved" legible.
 *
 * REMOUNT CONTRACT: the caller keys this component by `slotId:playerRegistrationId`.
 * That is what makes `playerRegistrationId` safe to capture once and reuse at
 * save: if the slot is reassigned under an open drawer, the key changes, this
 * component remounts, an unsaved draft about player A is discarded, and the new
 * instance is bound to player B. The server's 409 on a stale occupant is the
 * backstop, not the primary defence — without the remount, A's typed draft could
 * be saved against B before any request is made.
 */
const SlotNoteEditor = ({
  note,
  playerRegistrationId,
  onSave,
  saving = false,
}: SlotNoteEditorProps) => {
  'use memo'
  const [draft, setDraft] = useState(note ?? '')
  const [error, setError] = useState<string | null>(null)

  const normalized = draft.trim() === '' ? null : draft
  const dirty = normalized !== (note ?? null)

  const submit = () => {
    setError(null)
    onSave(normalized, playerRegistrationId).catch((failure) =>
      setError(guardMessage(failure))
    )
  }

  return (
    <div
      data-testid='slot-note-editor'
      css={css`
        display: flex;
        flex-direction: column;
        gap: 6px;
      `}
    >
      <span css={labelStyle}>{balanceLabel('common:balanceSlotNote')}</span>
      <span css={hintStyle}>{balanceText('common:balanceSlotNoteHint')}</span>
      <Input.TextArea
        data-testid='slot-note-input'
        aria-label={balanceLabel('common:balanceSlotNote')}
        value={draft}
        rows={3}
        maxLength={NOTE_MAX_LENGTH}
        disabled={saving}
        placeholder={balanceText('common:balanceSlotNotePlaceholder')}
        onChange={(event) => setDraft(event.target.value)}
        /* `rows` stays as the FLOOR (three lines wherever this renders without
           spare height); the flex rule above is what grows it in the drawer. */
        /* SN-4709 (Inon, Aug 12) — roomier than the three rows it used to be,
           without stretching: the drawer centres the whole block instead, so a
           textarea that absorbed the leftover height would have fought it. */
        css={css`
          min-height: 220px;
          resize: vertical;
        `}
      />
      {error ? (
        <Alert
          type='error'
          showIcon
          message={error}
          data-testid='slot-note-error'
        />
      ) : null}
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
        `}
      >
        <Button
          size='small'
          type='primary'
          data-testid='slot-note-save'
          loading={saving}
          disabled={!dirty || saving}
          onClick={submit}
        >
          {balanceLabel('common:balanceSlotNoteSave')}
        </Button>
      </div>
    </div>
  )
}

export default SlotNoteEditor
